import { Typography } from '@material-ui/core';
import Chip from '@material-ui/core/Chip';
import LinearProgress from '@material-ui/core/LinearProgress';
import { withStyles } from '@material-ui/core/styles';
import ArrowRightIcon from '@material-ui/icons/ArrowRight';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import WarningIcon from '@material-ui/icons/Warning';
import PropTypes from 'prop-types';
import React from 'react';
import { withRouter } from 'react-router-dom';
import QualityCheck from '../application-core/QualityCheck';
import { globalStyles as styles } from '../core/style/gstheme';
import TitledPanel from '../core/TitledPanel';
import { makeCancelable } from '../core/utils/utils';
import Warning from '../core/Warning';
import GS from '../grafsync/grafsync';

const CACHE_TIME = 180000;

const views = [
    "classcycleclashes",
    "classpropertyissues",
    "disjointnessconsistency",
    "symmetricasymmetricinverses",
    "reflexiveirreflexive",
    "symmetricasymmetric"
];

const consistencyReport = [
    {
        prop: "disjointnessInconsistency",
        message: "The following classes are disjoint with themselves."
    },
    {
        prop: "subclassDisjointnessInconsistency",
        message: "The following classes are a subclass of a class and also disjoint with that class."
    },
    {
        prop: "multipleSubclassDisjointnessInconsistency",
        message: "The following classes have multiple subclass definitions that are in conflict via disjointness."
    },
    {
        prop: "disjointnessEquivalenceInconsistency",
        message: "The following classes are both disjoint and equivalent."
    }
];


class OntologyQuality extends React.Component {

    constructor(props) {
        super(props);
        const filteredViews = views;
        let initialViewStates = {};
        filteredViews.forEach(fv => initialViewStates[fv] = undefined);
        const intiialState = Object.assign({
            viewCount: 0,
            isLoading: true,
            isErrored: false,
            isFailed: false,
            filteredViews
        }, initialViewStates);
        this.state = intiialState;
        this.cancellable = null;
    }

    componentDidMount() {
        this.loadViews();
    }

    async loadViews(refreshIfStale = true) {
        // We run linearly to reduce strain on the API
        const { filteredViews } = this.state;
        const currentTime = Date.now();
        const id = this.props.id;
        for (let i = 0; i < filteredViews.length; i++) {
            const view = filteredViews[i];
            const viewID = view + "-" + this.props.graph + "-" + id;
            const cachedData = GS.retrieveCache(viewID);
            if ((!refreshIfStale && cachedData) || (cachedData && cachedData.time > currentTime - CACHE_TIME)) {
                this.setState((prevState) => ({
                    [view]: cachedData.data || undefined,
                    [view + "-time"]: cachedData.time,
                    viewCount: prevState.viewCount + 1
                }));
            }
            else {
                GS.clearCache(viewID);
                await this.loadData(view, i)
                    .promise
                    .then((ontologyData) => {
                        if (this.cancellable && this.cancellable.isCancelled()) return;
                        const viewTime = Date.now();
                        if (Object.keys(ontologyData).length > 0) {
                            const data = ontologyData.graph ? [...ontologyData.graph] : [ontologyData];
                            GS.cache(viewID, data);
                            this.setState({ [view]: data, [view + "-time"]: viewTime });
                        }
                        else {
                            GS.cache(viewID, null);
                            this.setState({ [view]: undefined, [view + "-time"]: viewTime });
                        }
                        this.setState((prevState) => ({
                            viewCount: prevState.viewCount + 1
                        }));
                    })
                    .catch(() => {
                        if (this.cancellable && !this.cancellable.isCancelled()) this.setState({
                            isErrored: true,
                            isLoading: false,
                            isFailed: true,
                            message: "Could not load all of the data for the quality view."
                        });
                        i = filteredViews.length;
                    });
            }
        }
        if (this.cancellable && this.cancellable.isCancelled()) return;
        this.setState({ isLoading: false });
    }

    onExitWarning = () => this.setState({ isErrored: false });

    loadData(view, index) {
        this.cancellable = makeCancelable(
            GS.export(view, this.props.id, this.props.graph)
        );
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
    }

    onSelect(link) {
        this.props.history.push(link);
        this.props.onToggleView();
    }

    onRefresh = (view) => {
        this.setState({
            viewCount: 0,
            isLoading: true
        }, () => {
            GS.clearCache(view + "-" + this.props.graph + "-" + this.props.id);
            this.loadViews(false);
        });
    };

    render() {

        const { classes, link } = this.props;
        const { isErrored, message, viewCount, isFailed, isLoading, reflexiveirreflexive, symmetricasymmetric, filteredViews,
            symmetricasymmetricinverses, classpropertyissues, classcycleclashes, disjointnessconsistency } = this.state;

        if (isErrored) return <Warning message={message} onExitWarning={this.onExitWarning} />;
        if (isFailed) return null;

        let viewItems = views.length;
        // If all checks are good then there will no state with errors for any of the views
        const checksSuccessful = !filteredViews.some(fv => this.state[fv]);

        return (
            <div style={{ margin: '8px' }}>
                <TitledPanel title="">
                    <div style={{ height: '93px', paddingTop: '40px' }}>
                        {viewCount < viewItems &&
                            <Typography color="secondary" paragraph className={classes.mainHeading} align="center">{viewCount}/{viewItems} Checks Completed</Typography>
                        }
                        {viewCount === viewItems &&
                            (checksSuccessful ?
                                <Typography
                                    paragraph
                                    className={classes.mainHeadingSecondary}
                                    style={{ color: '#50832C' }}
                                    align="center">
                                    <CheckCircleIcon
                                        data-test="checks-passed"
                                        fontSize="large"
                                        color="secondary"
                                        style={{ verticalAlign: 'middle', marginRight: '10px' }} />Checks Successful</Typography>
                                :
                                <Typography
                                    data-test="checks-failed"
                                    paragraph
                                    className={classes.mainHeading}
                                    style={{ color: '#DD2C00' }}
                                    align="center">
                                    <WarningIcon
                                        data-test="checks-failed"
                                        fontSize="large"
                                        style={{ verticalAlign: 'middle', marginRight: '10px' }} />Checks Failed</Typography>
                            )}
                        <LinearProgress color="secondary" className={classes.progress} variant="determinate" value={viewCount / viewItems * 100} />
                    </div>
                </TitledPanel>
                <Typography className={classes.mainHeading} style={{ marginTop: '20px' }}>Quality Check Results</Typography>
                <Typography paragraph variant="body2" style={{ marginBottom: 36 }}>Results will be stored for three minutes. If you make fixes within that amount of time you may want to refresh that check to see the latest results.</Typography>
                <QualityCheck
                    dataTest="class-cycle-clashes"
                    title="Class Cycle Clashes"
                    issues={classcycleclashes}
                    isLoading={isLoading}
                    onRefresh={() => this.onRefresh("classcycleclashes")}
                    fromCache={this.state["classcycleclashes-time"]}
                    issueDisplay={
                        (cc) => cc.cyclicSubclass && cc.cyclicSubclass.map((v, vIndex) => {
                            return <div data-test={"item-" + vIndex} key={v} style={{ marginBottom: 10 }}>
                                <Chip color="primary"
                                    data-test="source"
                                    className={classes.activeChip}
                                    clickable
                                    label={cc.latestIRI || cc.id}
                                    onClick={() => this.onSelect(link + "&view=class&class=" + cc.id)} />
                                <CompareArrowsIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                <Chip color="primary"
                                    data-test="target"
                                    className={classes.activeChip}
                                    clickable
                                    label={v}
                                    onClick={() => this.onSelect(link + "&view=class&class=" + v)} />
                            </div>;
                        })
                    }
                />
                <QualityCheck
                    dataTest="class-property-issues"
                    title="Classes With Properties Where Classes Expected"
                    issues={classpropertyissues}
                    isLoading={isLoading}
                    onRefresh={() => this.onRefresh("classpropertyissues")}
                    fromCache={this.state["classpropertyissues-time"]}
                    issueDisplay={
                        (cc) => cc.classPropertyIssue && [...cc.classPropertyIssue].sort().map((v, vIndex) => {
                            return <div data-test={"item-" + vIndex} key={v} style={{ marginBottom: 10 }}>
                                <Chip color="primary"
                                    data-test="source"
                                    className={classes.activeChip}
                                    clickable
                                    label={cc.latestIRI || cc.id}
                                    onClick={() => this.onSelect(link + "&view=class&class=" + cc.id)} />
                                <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                <Chip color="primary"
                                    data-test="target"
                                    className={classes.labelChip}
                                    clickable={false}
                                    label={v} />
                            </div>;
                        })
                    }
                />
                <QualityCheck
                    dataTest="disjointness-consistency"
                    title="Disjointness and Class Equivalence Inconsistency"
                    issues={disjointnessconsistency}
                    isLoading={isLoading}
                    onRefresh={() => this.onRefresh("disjointnessconsistency")}
                    fromCache={this.state["disjointnessconsistency-time"]}
                    issueDisplay={
                        (li, liList) => {
                            return consistencyReport.map((lr, lrIndex) =>
                                li[lr.prop] &&
                                <div data-test={"lr-" + lrIndex} key={li.id + "-" + lr.prop} style={{ marginBottom: 10 }}>
                                    <Typography data-test="qm" variant="body2" paragraph>{lr.message}</Typography>
                                    <Chip
                                        color="primary"
                                        data-test="qm-resource"
                                        className={classes.activeChip}
                                        clickable
                                        label={li.id}
                                        onClick={() => this.onSelect(link + "&view=class&class=" + li.id)} />
                                    <ArrowRightIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                    {li[lr.prop].sort().map((v, vIndex) => {
                                        const vEntry = liList.find(liEntry => liEntry.id === v);
                                        return <Chip
                                            color="primary"
                                            className={classes.activeChip}
                                            disabled={!vEntry || !vEntry.qualityResourceExists}
                                            clickable={vEntry && vEntry.qualityResourceExists}
                                            data-test={"qm-chip-" + vIndex}
                                            key={"chip-" + vIndex}
                                            onClick={() => this.onSelect(link + "&view=class&class=" + v)}
                                            label={v} />;
                                    })}
                                </div>
                            );
                        }

                    }
                />
                <QualityCheck
                    dataTest="symmetric-assymetric-issues"
                    title="Symmetric Properties With Asymmetric Inverses"
                    issues={symmetricasymmetricinverses}
                    isLoading={isLoading}
                    onRefresh={() => this.onRefresh("symmetricasymmetricinverses")}
                    fromCache={this.state["symmetricasymmetricinverses-time"]}
                    issueDisplay={
                        (cc) => cc.symmetricAsymmetricInverse && [...cc.symmetricAsymmetricInverse].sort().map((v, vIndex) => {
                            return <div data-test={"item-" + vIndex} key={v} style={{ marginBottom: 10 }}>
                                <Chip color="primary"
                                    data-test="source"
                                    className={classes.activeChip}
                                    clickable
                                    label={cc.latestIRI || cc.id}
                                    onClick={() => this.onSelect(link + "&view=objectProperty&objectProperty=" + cc.id)} />
                                <CompareArrowsIcon style={{ verticalAlign: 'text-bottom', marginLeft: 5, marginRight: 10 }} />
                                <Chip color="primary"
                                    data-test="target"
                                    className={classes.labelChip}
                                    clickable={false}
                                    label={v} />
                            </div>;
                        })
                    }
                />
                <QualityCheck
                    dataTest="reflexive-irreflexive"
                    title="Properties That Are Both Reflexive And Irreflexive"
                    issues={reflexiveirreflexive}
                    isLoading={isLoading}
                    onRefresh={() => this.onRefresh("reflexiveirreflexive")}
                    fromCache={this.state["reflexiveirreflexive-time"]}
                    issueDisplay={
                        (cc) => cc.conflictingTypes ?
                            <div data-test={"item"} style={{ marginBottom: 10 }}>
                                <Chip color="primary"
                                    data-test="source"
                                    className={classes.activeChip}
                                    clickable
                                    label={cc.latestIRI || cc.id}
                                    onClick={() => this.onSelect(link + "&view=objectProperty&objectProperty=" + cc.id)} />
                            </div>
                            :
                            null
                    }
                />
                <QualityCheck
                    dataTest="symmetric-asymmetric"
                    title="Properties That Are Both Symmetric And Asymmetric"
                    issues={symmetricasymmetric}
                    isLoading={isLoading}
                    onRefresh={() => this.onRefresh("symmetricasymmetric")}
                    fromCache={this.state["symmetricasymmetric-time"]}
                    issueDisplay={
                        (cc) => cc.conflictingTypes ?
                            <div data-test={"item"} style={{ marginBottom: 10 }}>
                                <Chip color="primary"
                                    data-test="source"
                                    className={classes.activeChip}
                                    clickable
                                    label={cc.latestIRI || cc.id}
                                    onClick={() => this.onSelect(link + "&view=objectProperty&objectProperty=" + cc.id)} />
                            </div>
                            :
                            null
                    }
                />
            </div>
        );
    }
}

OntologyQuality.propTypes = {
    onToggleView: PropTypes.func.isRequired,
    id: PropTypes.string.isRequired,
    data: PropTypes.object.isRequired,
    link: PropTypes.string.isRequired,
    history: PropTypes.object,
    graph: PropTypes.string.isRequired,
    classes: PropTypes.object
};

export default withStyles(styles)(withRouter(OntologyQuality));